import { Button } from '@material-ui/core';
import clsx from 'clsx';
import React, { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { CRM_CLIENTS_PAGE } from 'src/constants/pathConsts';
import MenuLogo from 'src/features/drawer/MenuLogo';
import useNavigate from 'src/hooks/useNavigate';
import { useAppSelector } from 'src/hooks/useStore';
import { ArrowRightIcon } from 'src/legacy/components/Icons';
import { PageCallout } from 'src/legacy/components/PageCallout';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';

import IconTile from '../IconTile';

const isReactComponent = (
  value: React.ReactNode,
): value is React.ComponentType<any> => {
  return typeof value === 'function';
};

const StandardPageNavbar: FC<{ pageTitle: ReactNode }> = ({ pageTitle }) => {
  const headerStyles = clsx(
    'box-border flex items-center w-full border-0 border-b border-solid border-secondary-hover gap-3 px-4 h-[52px] xs:px-9 xs:h-[61px]',
  );

  return (
    <div className={headerStyles}>
      <MenuLogo />
      <BaseTypography fontType="13Medium">{pageTitle}</BaseTypography>
    </div>
  );
};
export interface StandardPageProps {
  pageTitle?: ReactNode;
  hasNavbar?: boolean;
  header?: string;
  title?: string;
  topIcon?: React.ReactNode;
  showTopIcon?: boolean;
  desc?: string;
  learnMoreLinkUrl?: string;
  startIconForActionButton?: React.ReactNode;
  endIconForActionButton?: React.ReactNode;
  labelForActionButton?: React.ReactNode;
  onAction?: () => void;
  actionButton?: React.ReactNode;
  noClientTitle?: string;
  noClientDesc?: string;
  hideGoToCRMButton?: boolean;
  titleForClient?: string;
  descForClient?: string;
  actionButtonForClient?: React.ReactNode;
  showContent?: boolean;
  navAction?: {
    path: string;
    label: string;
  };
  defaultMode?: boolean; // if true don't apply any settings based on client or internal user, and only uses the base properties i.e. title, topIcon, desc and actionButton.... rest are ignored
  disableHideChannelListToolbar?: boolean;
  // Overriders for default actions
  customStandardPageActions?: React.ReactNode;
}

export const StandardPage: React.FC<StandardPageProps> = ({
  pageTitle,
  title,
  topIcon,
  showTopIcon = true,
  desc,
  learnMoreLinkUrl,
  startIconForActionButton,
  endIconForActionButton,
  labelForActionButton,
  onAction,
  actionButton,
  noClientTitle = '',
  noClientDesc = '',
  hideGoToCRMButton = false,
  titleForClient = '',
  descForClient = '',
  actionButtonForClient,
  showContent = false,
  navAction,
  children,
  defaultMode = false,
  customStandardPageActions,
  hasNavbar = false, // channels layouts should use standard page navbar
}) => {
  const isClient = useAppSelector((state) => state.user.isClient);
  const { t } = useTranslation();
  const { navigate } = useNavigate();
  const activeClients = useAppSelector((state) => state.clients.activeClients);
  if (isClient && !titleForClient && !defaultMode) return null;

  let titleToUse = title;
  let descToUse = desc;
  let actionBtnToUse = actionButton;

  if (isClient && !defaultMode) {
    titleToUse = titleForClient;
    descToUse = descForClient;
    actionBtnToUse = actionButtonForClient;
  } else if ((!activeClients || activeClients.length < 1) && !defaultMode) {
    if (noClientTitle) {
      titleToUse = noClientTitle;
      descToUse = noClientDesc;
      actionBtnToUse = hideGoToCRMButton ? null : (
        <Button
          color="primary"
          variant="contained"
          endIcon={<ArrowRightIcon />}
          onClick={() => navigate(CRM_CLIENTS_PAGE.path)}
        >
          {t(($) => $.actions.go_to_item, {
            item: t(($) => $.client, { count: 2 }),
          })}
        </Button>
      );
    }
  }

  const renderActionButton = () => {
    if (actionBtnToUse) {
      return actionBtnToUse;
    }

    if (navAction) {
      return (
        <Button
          color="primary"
          variant="contained"
          endIcon={<ArrowRightIcon />}
          onClick={() => navigate(navAction.path)}
        >
          {`Go to ${navAction.label}`}
        </Button>
      );
    }

    // There are actions that either do not use the same styles or have multiple buttons
    // If customStandardPageActions are passed in, use them instead of the default action button
    if (customStandardPageActions) return customStandardPageActions;

    if (onAction) {
      return (
        <Button
          variant="contained"
          color="primary"
          {...(startIconForActionButton
            ? { startIcon: startIconForActionButton }
            : {})}
          {...(endIconForActionButton
            ? { startIcon: endIconForActionButton }
            : {})}
          onClick={onAction}
        >
          {labelForActionButton}
        </Button>
      );
    }

    return null;
  };

  const contentStyles = clsx(
    'box-border flex justify-center items-center relative w-full',
    defaultMode ? 'h-[80%]' : 'h-full',
    hasNavbar ? 'px-4' : 'p-[inherit]',
  );

  return showContent ? (
    // needed to satisfy React.FC return type

    <>{children}</>
  ) : (
    <div className="flex flex-col h-full w-full">
      {hasNavbar && <StandardPageNavbar pageTitle={pageTitle || ''} />}
      <div className={contentStyles}>
        <PageCallout
          title={titleToUse || ''}
          description={descToUse || ''}
          link={!isClient ? learnMoreLinkUrl || '' : ''}
          linkText="Learn more"
          actionButtonRenderer={renderActionButton}
        >
          {showTopIcon && topIcon && (
            <IconTile
              icon={
                isReactComponent(topIcon)
                  ? React.createElement(topIcon)
                  : topIcon
              }
            />
          )}
        </PageCallout>
      </div>
    </div>
  );
};
